<script lang="ts" setup>
import { computed } from 'vue';
import { $fetch } from '@/home/plugins/fetch';
import { useAsyncData } from '@/home/hooks/async-data';
import InfiniteMarquee from '@/home/components/InfiniteMarquee.vue';
import CountTo from '@/home/components/CountTo.vue';

const { data: incubateList } = useAsyncData('home:incubate', () => $fetch('/project/v1/list', { data: { status: 2, limit: 6 } }));
const { data: donateList } = useAsyncData('home:donate', () => $fetch('/project/v1/list', { data: { status: 1 } }));
const { data: infoList } = useAsyncData('home:info', () => $fetch('/statistic/v1/major'));

const donateMarqueeList = computed(() => [
  donateList.value?.slice(0, 3) ?? [],
  donateList.value?.slice(3, 6) ?? [],
]);
</script>

<template>
  <section class="page-home-projects">
    <div class="mx-auto max-w-1200 py-100">
      <div class="flex items-center justify-between">
        <h2 class="relative m-0 text-40 text-#292D33 font-600 leading-56 after:absolute after:bottom--16 after:left-0 after:right-0 after:h-4 after:bg-#1281FF after:content-empty">
          {{ $t('home.exploreProjects.ourProject') }}
        </h2>
        <RouterLink to="/projects" class="flex-y-center flex-shrink-0 gap-x-8 text-16 text-#1281FF font-500 no-underline">
          <span>{{ $t('home.exploreProjects.allItems') }}</span>
          <span class="i-home-icons:arrow-right-2 block size-24"></span>
        </RouterLink>
      </div>

      <div class="mt-69 flex gap-x-80">
        <div class="flex flex-grow justify-between *:max-w-200 *:flex-shrink-0" v-if="infoList">
          <div>
            <div class="text-50 text-#1281FF font-800 leading-56">
              <CountTo
                :end-val="infoList.incubatingCount >= 100000 ? infoList.incubatingCount / 10000 : infoList.incubatingCount"
                :suffix="infoList.incubatingCount >= 100000 ? '万+' : '+'"
              />
            </div>
            <div class="mt-10 truncate text-16 text-#9497A1 font-400 leading-28">
              {{ $t('home.info.numberIntroduce1') }}
            </div>
          </div>
          <div>
            <div class="text-50 text-#1281FF font-800 leading-56">
              <CountTo
                :end-val="infoList.storageCount >= 100000 ? infoList.storageCount / 10000 : infoList.storageCount"
                :suffix="infoList.storageCount >= 100000 ? '万+' : '+'"
              />
            </div>
            <div class="mt-10 truncate text-16 text-#9497A1 font-400 leading-28">
              {{ $t('home.info.numberIntroduce2') }}
            </div>
          </div>
          <div>
            <div class="text-50 text-#1281FF font-800 leading-56">
              <CountTo
                :end-val="infoList.committerCount >= 100000 ? infoList.committerCount / 10000 : infoList.committerCount"
                :suffix="infoList.committerCount >= 100000 ? '万+' : '+'"
              />
            </div>
            <div class="mt-10 truncate text-16 text-#9497A1 font-400 leading-28">
              {{ $t('home.info.numberIntroduce3') }}
            </div>
          </div>
          <div>
            <div class="text-50 text-#1281FF font-800 leading-56">
              <CountTo
                :end-val="infoList.starCount >= 10000 ? infoList.starCount / 1000 : infoList.starCount"
                :suffix="infoList.starCount >= 10000 ? '万+' : '+'"
              />
            </div>
            <div class="mt-10 truncate text-16 text-#9497A1 font-400 leading-28">
              {{ $t('home.info.numberIntroduce4') }}
            </div>
          </div>
        </div>

        <div class="w-280 text-20 text-#9497A1 font-400 leading-30">
          {{ $t('home.exploreProjects.desc1') }}
        </div>
      </div>

      <div class="mt-32 flex-center">
        <template v-for="item in incubateList" :key="item.id">
          <RouterLink :to="`/project/${item.id}`" class="block h-132 w-185 p-15" v-if="item.detailLogo || item.cardLogo">
            <img :data-src="item.detailLogo || item.cardLogo" class="lazyload block size-full object-contain" :alt="item.title">
          </RouterLink>
        </template>
      </div>

      <div class="mt-32">
        <InfiniteMarquee v-for="(item, index) in donateMarqueeList" :key="index" :speed="40" :reverse="index % 2 ? true : false" class="not-first:mt-40">
          <div v-for="piece in item" :key="piece.id" class="group mx-20 h-134 w-348 flex-center border-1 border-#D6DDEB rounded-8 border-solid hover:bg-#1281FF">
            <RouterLink :to="`/project/${piece.id}`" class="block size-full p-32 no-underline">
              <h3 class="my-0 truncate text-24 text-#2D3339 font-500 leading-1.2em group-hover:text-white" :title="piece.title">
                {{ piece.title }}
              </h3>
              <p class="my-0 mt-12 flex-y-center justify-between gap-x-8 text-18 font-400 leading-1.6em" :title="piece.projectValue">
                <span class="truncate text-#4C5763 group-hover:text-white">{{ piece.projectValue }}</span>
                <span class="i-home-icons:arrow-right-2 block size-24 flex-shrink-0 text-#2D3339 group-hover:text-white"></span>
              </p>
            </RouterLink>
          </div>
        </InfiniteMarquee>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
</style>
